import React, { useState, useRef, useEffect } from 'react';
import { Bell, CheckCheck } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { isSalesRole } from '../lib/sales-access';
import { formatDistanceToNow } from 'date-fns';

/**
 * The row's age, or '' when it has no usable timestamp.
 *
 * Absent is rendered as nothing rather than a guess — this is a caption under a
 * line of text, not a data field, so a dash would read as a value. What must
 * NOT happen is `new Date(undefined)`, which is an Invalid Date and prints
 * "Invalid Date" straight onto the screen.
 */
export function formatNotificationTime(notification: any): string {
  const raw = notification?.createdAt ?? notification?.created_at;
  if (!raw) return '';
  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return '';
  return formatDistanceToNow(dt, { addSuffix: true });
}

/**
 * Which route tree the reader is standing in. A bell link is only a link if
 * the path is registered in THAT tree — `AuthenticatedRoutes` in App.tsx
 * returns exactly one <Routes> block per user, and a path missing from it
 * falls through to the catch-all redirect, which reads as a broken tap.
 *
 *   office  — AppLayout tree (admin, dispatcher, supervisor, office_staff)
 *   field   — the /field tree (casing, PT, downhole hands)
 *   my-work — the /my-work tree (trucking drivers, rental techs). It routes
 *             /field/time-off and /field/clock-in and nothing else this bell
 *             could point at.
 *
 * The field/my-work split mirrors App.tsx's own test (`hasVertical('trucking')
 * || hasVertical('surface_rentals')`); if that test changes, change this.
 */
export type BellAudience = 'office' | 'field' | 'my-work';

export function getBellAudience(
  isFieldWorker: boolean,
  hasVertical: (key: string) => boolean,
): BellAudience {
  if (!isFieldWorker) return 'office';
  return hasVertical('trucking') || hasVertical('surface_rentals') ? 'my-work' : 'field';
}

/**
 * Where a Scout nudge opens (fieldiq-api scout-nudge-delivery.service.ts,
 * api#1083): a row of type 'other' with `data.event === 'scout_nudge'` and
 * snake_case ids inside `data`.
 *
 *   follow_up_due / follow_up_overdue → the MEETING the follow-up was promised
 *     in (`activity_id`), where the rep marks it done or reads the next step
 *   customer_quiet → the CLIENT (`customer_id`); a quiet customer has no
 *     activity by construction (the API's scout_nudges_quiet_has_no_activity)
 *
 * A follow-up whose row lacks its activity falls back to the client rather
 * than to nothing: the client is still the right place, and a dead row is
 * the worse answer. No usable id at all → null, so the row renders as text.
 * Keyed on `data.event`, never on `type` — 'other' is shared with every
 * other unrouted notification.
 */
export function scoutNudgeRoute(data: any): string | null {
  if (data?.event !== 'scout_nudge') return null;
  const id = (v: unknown) => (typeof v === 'string' && v.trim() !== '' ? v : null);
  const activityId = id(data.activity_id);
  const customerId = id(data.customer_id);
  const isFollowUp = data.nudge_type === 'follow_up_due' || data.nudge_type === 'follow_up_overdue';
  if (isFollowUp && activityId) return `/sales/meetings/${encodeURIComponent(activityId)}`;
  return customerId ? `/customers/${encodeURIComponent(customerId)}` : null;
}

/** Read an id off the row's column (either casing) or its data payload. */
function relatedJob(notification: any): string | undefined {
  return notification.relatedJobId ?? notification.related_job_id ?? notification.data?.job_id ?? undefined;
}

/**
 * Where a FIELD reader's click goes, or null when the field app has no screen
 * for it — and null renders the row as text, never as a dead link.
 *
 * job_assigned and ticket_* rows written before the API began attaching
 * related_job_id / data.job_id (October 2026) carry only a job or ticket
 * NUMBER. They were not backfilled, so they have no destination and stay
 * unclickable rather than being resolved by number here.
 * There is no field ticket screen: a ticket opens on its job, where the field
 * app edits it.
 */
function getFieldRoute(notification: any, audience: 'field' | 'my-work'): string | null {
  const { type, data } = notification;
  const event = data?.event;

  if (type === 'system') {
    if (event === 'time_off_reviewed' || event === 'time_off_dates_changed') return '/field/time-off';
    return null;
  }
  if (type === 'other') {
    if (event === 'timesheet_rejected' && data?.timesheet_id) {
      return `/field/clock-in?timesheet=${encodeURIComponent(data.timesheet_id)}`;
    }
    if (audience === 'field' && event === 'pt_certificate_rejected' && data?.job_id) {
      return `/field/pt/jobs/${data.job_id}`;
    }
    return null;
  }

  // Everything below is only routed in the /field tree.
  if (audience !== 'field') return null;

  switch (type) {
    case 'cert_rejected':
      return '/field/certs';
    case 'job_assigned':
    case 'ticket_approved':
    case 'ticket_rejected': {
      const jobId = relatedJob(notification);
      return jobId ? `/field/jobs/${jobId}` : null;
    }
    default:
      return null;
  }
}

export function getNotificationRoute(notification: any, audience: BellAudience = 'office'): string | null {
  if (audience !== 'office') return getFieldRoute(notification, audience);

  const { type, data } = notification;
  // The API reads through Drizzle and returns camelCase; tolerate both.
  const relatedTicketId = notification.relatedTicketId ?? notification.related_ticket_id;
  const relatedJobId = notification.relatedJobId ?? notification.related_job_id;

  // 'system' type with an event discriminator — check data.event before the switch.
  // The office reads its own requests on /time-off; /field/time-off is not in
  // the office route tree and fell through to the catch-all redirect.
  // A NEW request reaches reviewers the same way: the API writes it as
  // 'system' + event 'time_off_submitted', never as a type of that name.
  if (type === 'system') {
    if (
      data?.event === 'time_off_submitted'
      || data?.event === 'time_off_reviewed'
      || data?.event === 'time_off_dates_changed'
    ) return '/time-off';
    return null;
  }

  // A rejected timesheet is the reader's OWN shift. ClockInScreen opens it
  // from ?timesheet=; the office mounts that screen at /payroll/clock-in, and
  // /field/clock-in is not in the office route tree.
  if (type === 'other') {
    if (data?.event === 'timesheet_rejected' && data?.timesheet_id) {
      return `/payroll/clock-in?timesheet=${encodeURIComponent(data.timesheet_id)}`;
    }
    if (data?.event === 'scout_nudge') return scoutNudgeRoute(data);
    return null;
  }

  switch (type) {
    case 'cert_review_requested':
      return data?.cert_id ? `/cert-review?highlight=${data.cert_id}` : '/cert-review';
    // The office has no screen for its own certs; /field/certs is not in its
    // route tree. The review queue is the office's cert page. No ?highlight=:
    // the queue lists PENDING certs, and a rejected one is not among them.
    case 'cert_rejected':
      return '/cert-review';
    // An email address went bad. Deep-link to the DOCUMENT that carried it —
    // the thing whose address needs correcting — never to a generic list.
    // No document (a digest, an internal send) means no link.
    case 'email_delivery_failed':
      if (relatedTicketId) return `/tickets/${relatedTicketId}`;
      if (relatedJobId) return `/jobs/${relatedJobId}`;
      if (data?.entity_type === 'invoice' && data?.entity_id) return `/invoices/${data.entity_id}`;
      if (data?.entity_type === 'quote' && data?.entity_id) return `/quotes/${data.entity_id}`;
      return null;
    // A crew corrected a casing spec (fieldiq-api 0400). The job is the thing
    // to look at — the corrected string, who changed it, and the tickets the
    // message names all hang off it.
    case 'casing_spec_corrected':
      if (relatedJobId) return `/jobs/${relatedJobId}`;
      return data?.job_id ? `/jobs/${data.job_id}` : null;
    case 'job_assigned':
      return data?.job_id ? `/jobs/${data.job_id}` : null;
    // A job chat message (fieldiq-api job-chat-notify, api#1310). ?chat=1 is
    // what JobDetail reads to open with the chat showing — the same URL the
    // push carries. The API writes this row for office roles only, so the
    // field tree has no case for it.
    case 'job_chat_message': {
      const jobId = relatedJobId ?? data?.job_id;
      return jobId ? `/jobs/${jobId}?chat=1` : null;
    }
    case 'ticket_rejected':
    case 'ticket_approved':
      return data?.ticket_id ? `/tickets/${data.ticket_id}` : null;
    case 'safety_alert':
      return '/safety';
    case 'time_off_submitted':
      return '/time-off';
    default:
      return null;
  }
}

/**
 * A small kind label above the row's title, for rows whose title alone does
 * not say what they are. A job chat row's title is "<sender> · <job number>"
 * (job-chat-notify), which reads as a name, not a message. Null for every
 * other type: those titles already name themselves.
 */
export function notificationKindLabel(notification: any): string | null {
  return notification?.type === 'job_chat_message' ? 'Job chat' : null;
}

/**
 * `panelClassName` replaces the dropdown's POSITION and WIDTH only (the default
 * is `absolute right-0 top-full mt-2 w-80`, which the office header keeps).
 * The /my-work header passes a phone-safe one: anchored to the bell's right
 * edge at a fixed 320px, the panel started 30px off-screen on a 360px phone
 * (fe#815).
 */
export default function NotificationBell({ panelClassName }: { panelClassName?: string } = {}) {
  const { user, isFieldWorker, hasVertical } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const isReviewer = ['admin', 'dispatcher', 'supervisor', 'office_staff'].includes(user?.role);
  // Field hands see their own notifications too (the API serves any user's
  // own rows). Their clicks route through the field tree — see getBellAudience.
  const audience = getBellAudience(isFieldWorker, hasVertical);
  // Sales reads its own bell since api#1083 put the five /notifications routes
  // on the sales allowlist — Scout's nudges arrive there. Sales is an office
  // reader: its rows open in the office tree.
  const showBell = isReviewer || isFieldWorker || isSalesRole(user?.role);

  const { data: countData } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: () => api.notifications.unreadCount().then(r => r.data?.data ?? r.data ?? { count: 0 }),
    refetchInterval: 30_000,
    enabled: showBell,
  });

  const { data: notificationsData } = useQuery({
    queryKey: ['notifications', { unread: true }],
    queryFn: () => api.notifications.list({ unread: true, limit: 20 }).then(r => r.data?.data ?? r.data ?? []),
    enabled: open && showBell,
  });

  const markReadMutation = useMutation({
    mutationFn: (id: string) => api.notifications.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => api.notifications.readAll(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const unreadCount = countData?.count ?? 0;
  const notifications: any[] = Array.isArray(notificationsData) ? notificationsData : [];

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!showBell) return null;

  const handleNotificationClick = (notification: any) => {
    markReadMutation.mutate(notification.id);
    setOpen(false);
    const route = getNotificationRoute(notification, audience);
    if (route) {
      navigate(route);
    }
  };

  const handleMarkAllRead = () => {
    markAllReadMutation.mutate();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(prev => !prev)}
        aria-label="Notifications"
        className="group p-2.5 bg-secondary border border-border rounded-lg hover:bg-accent transition-colors relative"
      >
        {/* The hover fill is amber in both themes, so the amber icon vanished
            into it. On the fill it takes the dark ink every signal fill
            carries; the signal hue is a fill, never an ink. The resting colour
            is the accent's SIGNAL token, not the plain accent class: light
            theme pins that class with an important rule, which no hover
            utility can beat. The signal token reads the same amber in light
            and the same gold in dark, through the variable alone. */}
        <Bell size={22} data-testid="notification-bell-icon" className="text-fieldiq-accent-signal group-hover:text-fi-signal-ink transition-colors" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-panel" className={`${panelClassName ?? 'absolute right-0 top-full mt-2 w-80'} bg-fieldiq-card border border-fieldiq-border rounded-xl shadow-xl z-50 overflow-hidden`}>
          <div className="px-4 py-3 border-b border-fieldiq-border flex items-center justify-between">
            <h3 className="text-sm font-bold text-white">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <>
                  <button
                    onClick={handleMarkAllRead}
                    disabled={markAllReadMutation.isPending}
                    className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-fieldiq-accent/20 text-fieldiq-accent font-semibold hover:bg-fieldiq-accent/30 transition-colors disabled:opacity-50"
                  >
                    <CheckCheck size={10} />
                    {markAllReadMutation.isPending ? 'Clearing…' : 'Mark all read'}
                  </button>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 font-semibold">
                    {unreadCount} unread
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-fieldiq-muted">
                No unread notifications
              </div>
            ) : (
              notifications.map((n: any) => {
                const kindLabel = notificationKindLabel(n);
                const body = (
                  <>
                    {kindLabel && (
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-fieldiq-muted mb-0.5">{kindLabel}</p>
                    )}
                    <p className="text-sm text-white leading-snug">{n.title || n.message || 'New notification'}</p>
                    {n.body && <p className="text-xs text-fieldiq-muted mt-0.5 line-clamp-2">{n.body}</p>}
                    <p className="text-[10px] text-fieldiq-muted mt-1">
                      {/* The route reads through Drizzle and returns camelCase, so
                          `created_at` was undefined on every row and every
                          notification in this dropdown rendered with no time at
                          all. Same field-name drift the file already tolerates
                          for relatedTicketId in getNotificationRoute above —
                          read both, prefer the shape the API actually sends. */}
                      {formatNotificationTime(n)}
                    </p>
                  </>
                );
                const rowClass = 'w-full text-left px-4 py-3 border-b border-fieldiq-border/50 last:border-b-0';
                // A row with nowhere to go is text, not a button: a tap that
                // marks it read and lands nowhere is a dead link. Mark all read
                // still clears it.
                if (getNotificationRoute(n, audience) === null) {
                  return (
                    <div key={n.id} data-testid="notification-row-unlinked" className={rowClass}>
                      {body}
                    </div>
                  );
                }
                return (
                  <button
                    key={n.id}
                    onClick={() => handleNotificationClick(n)}
                    className={`${rowClass} hover:bg-fieldiq-dark/50 transition-colors`}
                  >
                    {body}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
